import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';

import ManagedInput from 'components/form/managed_input';
import Table from 'bundles/backoffice/vouchers/editor/table';
import _ from 'helpers/no_conflict_lodash';
import { BUY_SIDE, SELL_SIDE } from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { commissionModes, PERCENT_OF_RENT, FLAT_RATE, RATE_PER_SPACE_SIZE_UNIT } from './commission_modes';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { getCommissionAmount, getCommissionPercent, getCommissionRate } from './helpers/lease_term';
import { USD } from 'helpers/formatters/number_formats';

class LeaseStructureTerm extends React.Component {
  static propTypes = {
    commissionMode: PropTypes.oneOf(commissionModes).isRequired,
    controlWrapper: PropTypes.any,
    forceControlRendering: PropTypes.bool,
    hideNotes: PropTypes.bool,
    includeOperatingExpenses: PropTypes.bool,
    isFirst: PropTypes.bool,
    leaseTerm: PropTypes.shape({
      commissionAmount: PropTypes.number,
      commissionPercent: PropTypes.number,
      commissionRate: PropTypes.number,
      commissionUnits: PropTypes.string.isRequired,
      endDate: PropTypes.string,
      index: PropTypes.number.isRequired,
      leaseAmount: PropTypes.number,
      leaseRate: PropTypes.number,
      months: PropTypes.number,
      notes: PropTypes.string,
      startDate: PropTypes.string,
      operatingExpensesRate: PropTypes.number,
      transactionSideDetails: PropTypes.array.isRequired,
    }).isRequired,
    readOnly: PropTypes.bool,
    readOnlyActions: PropTypes.node,
    showCommissionColumns: PropTypes.bool.isRequired,
    showTransactionSides: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onInsert: PropTypes.func.isRequired
  }

  handleValueChange = (value, event) => {
    this.props.onChange(this.props.leaseTerm, event.target.name, value);
  }

  handleTransactionSideValueChange = (value, event, transactionSide) => {
    this.props.onChange(
      this.props.leaseTerm,
      event.target.name,
      value,
      transactionSide
    );
  }

  handleDelete = () => {
    this.props.onDelete(this.props.leaseTerm);
  }

  insertRowBefore = () => {
    const { startDate, index } = this.props.leaseTerm;
    this.props.onInsert(index, startDate, this.props.leaseTerm);
  }

  insertRowAfter = () => {
    const { endDate, index } = this.props.leaseTerm;
    this.props.onInsert(index + 1, moment(endDate).add(1, 'day').toISOString(), this.props.leaseTerm);
  }

  render() {
    const { endDate, leaseAmount, leaseRate, months, notes, operatingExpensesRate, startDate } = this.props.leaseTerm;
    const { hideNotes, showCommissionColumns, showTransactionSides } = this.props;
    const minEndDate = startDate ? moment(startDate) : null;
    const commissionColumnSides = showTransactionSides ? [SELL_SIDE, BUY_SIDE] : [null];
    const commissionAmountInputName = showTransactionSides ? 'grossAmount' : 'commissionAmount';

    return (
      <tr>
        {this.renderInput({ disabled: !this.props.isFirst, name: 'startDate', type: 'date', value: startDate })}
        {this.renderInput({ minDate: minEndDate, name: 'endDate', type: 'date', value: endDate })}

        {this.renderInput({
          ['aria-label']: 'lease-term-duration',
          disabled: this.props.leaseTerm.startDate == null,
          min: 0,
          name: 'months',
          type: 'number',
          value: months ? _.round(months, 1) : null
        })}

        {this.props.includeOperatingExpenses && this.renderInput({
          name: 'operatingExpensesRate',
          numeric: true,
          type: 'currency',
          value: operatingExpensesRate,
        })}

        {this.renderInput({
          allowNegative: true,
          name: 'leaseRate',
          numeric: true,
          type: 'currency',
          value: leaseRate,
        })}

        {this.renderInput({
          ['aria-label']: 'lease-term-total-rent',
          name: 'leaseAmount',
          numeric: true,
          type: 'currency',
          value: leaseAmount
        })}
        {commissionColumnSides.map(transactionSide => (
          <React.Fragment key={transactionSide || 'commission-columns'}>
            {this.props.commissionMode !== RATE_PER_SPACE_SIZE_UNIT && this.renderCommissionRate(transactionSide)}
            {
              showCommissionColumns &&
                this.renderInput({
                  ['aria-label']: transactionSide ? `${transactionSide}-commission-amount` : 'commission-amount',
                  name: commissionAmountInputName,
                  numeric: true,
                  transactionSide,
                  type: 'currency',
                  value: getCommissionAmount(this.props.leaseTerm, transactionSide)
                })
            }
          </React.Fragment>
        ))}
        {!hideNotes && this.renderInput({ name: 'notes', value: notes })}

        {this.renderActionControls()}
      </tr>
    );
  }

  renderActionControls() {
    if (this.props.readOnly && !this.props.forceControlRendering) {
      return <td>{this.props.readOnlyActions}</td>;
    }

    return <td className="actions-column">
      <div className="d-flex">
        {this.renderControl('insert-row-before', this.insertRowBefore, (
          <div className="table-action-icon add-row-before" />
        ))}

        {this.renderControl('insert-row-after', this.insertRowAfter, (
          <div className="table-action-icon add-row-after" />
        ))}

        {this.renderControl('delete-lease-term', this.handleDelete, (
          <FontAwesomeIcon className="clickable" icon={faTrashAlt} />
        ))}
      </div>
    </td>;
  }

  renderControl(name, onClick, control) {
    const ControlWrapper = this.props.controlWrapper || Div;
    return <ControlWrapper name={name} onClick={onClick}>{control}</ControlWrapper>;
  }

  renderCommissionRate(transactionSide) {
    const leaseTerm = this.props.leaseTerm;
    const commissionPercent = getCommissionPercent(leaseTerm, transactionSide);
    const commissionRate = getCommissionRate(leaseTerm, transactionSide);
    const commissionUnits = leaseTerm.commissionUnits;
    const percentInputName = transactionSide ? 'grossPercent' : 'commissionPercent';
    const rateInputName = transactionSide ? 'grossRate' : 'commissionRate';

    const { commissionMode } = this.props;

    if (commissionMode === FLAT_RATE) {
      return;
    }

    const formattedCommissionPercent = (commissionUnits === 'commission_units_percent' || commissionPercent == null)
      ? commissionPercent
      : _.round(commissionPercent, 2);

    if (commissionMode === PERCENT_OF_RENT) {
      return this.renderInput({
        ['aria-label']: transactionSide ? `${transactionSide}-commission-percent` : 'commission-percent',
        name: percentInputName,
        numeric: true,
        transactionSide,
        type: 'number',
        value: formattedCommissionPercent
      });
    } else {
      return this.renderInput({
        ['aria-label']: transactionSide ? `${transactionSide}-commission-rate` : 'commission-rate',
        name: rateInputName,
        numeric: true,
        transactionSide,
        type: 'currency',
        value: commissionRate
      });
    }
  }

  renderInput(props) {
    const { allowNegative, disabled, name, numeric, transactionSide, type, value, width, ...inputProps } = props;
    const { readOnly } = this.props;
    const onValueChange = transactionSide
      ? (value, event) => this.handleTransactionSideValueChange(value, event, transactionSide)
      : this.handleValueChange;

    let tooltipValue = value;
    if (type === 'currency') {
      tooltipValue = USD.format(value);
    } else if (type === 'date') {
      tooltipValue = formatDate(value);
    }

    return (
      <Table.td input key={name} numeric={numeric} readOnly={readOnly} tooltip={tooltipValue}>
        <ManagedInput
          allowNegative={allowNegative}
          disabled={disabled || readOnly}
          name={name}
          type={type}
          value={value}
          onValueChange={onValueChange}
          {...inputProps}
        />
      </Table.td>
    );
  }
}

function Div(props) {
  return <div {...props} />;
}

export default LeaseStructureTerm;
